// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Consent gate (G04, 2026-09-25; second purpose 2026-10-16) — distinct from lib/consent.ts's account-terms
 * record made at signup. GDPR + German TDDDG: every non-essential third-party script may load ONLY after an
 * explicit, un-pre-ticked opt-in recorded here. No default-on, no assumed consent from using the app.
 *
 * Two independent purposes, each its own localStorage key, so a visitor can accept one and refuse the other:
 *   - "marketing" (key `bb_marketing_consent`, historical name): product analytics — PostHog. This is the
 *     "Produktanalyse (PostHog)" switch; the key name predates the second purpose and stays so that nobody who
 *     already decided is asked again about it.
 *   - "ads" (key `bb_ads_consent`): ad measurement — the Meta Pixel. Shown as "Marketing (Meta Pixel)".
 */

export type MarketingConsent = "granted" | "denied" | "unset"
export type AdsConsent = MarketingConsent

interface ConsentPurpose {
  get(): MarketingConsent
  set(consent: "granted" | "denied"): void
  clear(): void
  onChange(listener: (consent: MarketingConsent) => void): () => void
}

function createPurpose(storageKey: string): ConsentPurpose {
  const listeners = new Set<(consent: MarketingConsent) => void>()
  return {
    get() {
      try {
        const v = window.localStorage.getItem(storageKey)
        if (v === "granted" || v === "denied") return v
      } catch {
        // localStorage unavailable (private mode, blocked) — treat as unset, never assume granted.
      }
      return "unset"
    },
    set(consent) {
      try {
        window.localStorage.setItem(storageKey, consent)
      } catch {
        // Non-fatal: listeners below still fire for the rest of this session.
      }
      for (const listener of listeners) listener(consent)
    },
    clear() {
      try {
        window.localStorage.removeItem(storageKey)
      } catch {
        // Non-fatal: listeners below still fire for the rest of this session.
      }
      for (const listener of listeners) listener("unset")
    },
    onChange(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

const marketing = createPurpose("bb_marketing_consent")
const ads = createPurpose("bb_ads_consent")

export const getMarketingConsent = marketing.get
export const setMarketingConsent = marketing.set
export const onMarketingConsentChange = marketing.onChange

export const getAdsConsent = ads.get
export const setAdsConsent = ads.set
export const onAdsConsentChange = ads.onChange

/**
 * Forgets the recorded choice and tells listeners the state is "unset" again, so the consent banner
 * re-opens. A visitor can change their mind at any time (GDPR Art. 7(3): withdrawing is as easy as giving).
 */
export const clearMarketingConsent = marketing.clear
export const clearAdsConsent = ads.clear

/** Public footer "Cookie-Einstellungen": forgets BOTH choices so the banner re-opens with both purposes. */
export function clearAllConsent(): void {
  marketing.clear()
  ads.clear()
}
